// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xngGaToolBarNavigationEvent from "@guestsupply/App/Handlers/GoogleAnalytics/ToolBarNavigation/ToolBarNavigationEvent";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getMainNavigationWidget } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@guestsupply/XDL/constants";
import { parseBoolean, xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-CHANGES
const mapStateToProps = (state: CustomAppState) => ({
    showSearchSectionOnMobile: (state as CustomAppState).xHeader.showSearchSectionOnMobile,
    isEducationCustomer: parseBoolean(getSession(state).properties?.isEducationCustomer),
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    mainNavigation: getMainNavigationWidget(state),
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
});

// xng-change: add google event
const mapDispatchToProps = {
    xngGaToolBarNavigationEvent,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${xScreen("lte", "md")} {
                flex-grow: 8;
            }
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

// XNG-CHANGES : Change in the structure
const LogoLinksSearch: React.FC<Props> = ({
    id,
    showSearchSectionOnMobile,
    isEducationCustomer,
    isMobileScreen,
    isTabletScreen,
    mainNavigation,
    isCheckoutPage,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
}) => {
    return (
        <StyledWrapper {...logoLinksSearchStyles.container}>
            {isTabletScreen &&
                !isCheckoutPage &&
                createWidgetElement("Header/MainNavigation", {
                    fields: { ...mainNavigation },
                })}
            {((!showSearchSectionOnMobile && isMobileScreen) || !isMobileScreen) && (
                <StyledWrapper
                    {...logoLinksSearchStyles.logoWrapper}
                    onClick={() =>
                        // xng-change: add google event
                        xngGaToolBarNavigationEvent({
                            clickText: "Logo",
                        })
                    }
                >
                    <Zone contentId={id} zoneName="Logo" />
                </StyledWrapper>
            )}
            {!isCheckoutPage && (
                <StyledWrapper
                    {...logoLinksSearchStyles.searchMenuWrapper}
                    onClick={() =>
                        // xng-change: add google event
                        xngGaToolBarNavigationEvent({
                            clickText: "Search",
                        })
                    }
                >
                    <Zone contentId={id} zoneName="Search" />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
